// Tree navigation and mutation helpers for the script JSON.
//
// The script array is: [startEvent, changeEvent, saveEvent]
// Each event is:       { type: "event/start", children: [...blocks] }
//
// A "path" is an array of indices navigating from the script root:
//   [0]           → the start event node itself
//   [0, 3]        → script[0].children[3]
//   [0, 5, 1]     → script[0].children[5].children[1]  (a wrapper node)
//   [0, 5, 1, 2]  → script[0].children[5].children[1].children[2]

export function getNode(script, path) {
  let node = script[path[0]];
  for (let i = 1; i < path.length; i++) {
    node = node.children[path[i]];
  }
  return node;
}

// Immutably apply an updater to the script.
// updater receives a deep-cloned copy and can mutate it freely.
export function updateScript(comps, compId, updater) {
  const newComps = JSON.parse(JSON.stringify(comps));
  updater(newComps[compId].script);
  return newComps;
}

// Insert a new block node into a container at a given index.
export function insertBlock(comps, compId, containerPath, index, newNode) {
  return updateScript(comps, compId, (script) => {
    const container = getNode(script, containerPath);
    container.children.splice(index, 0, JSON.parse(JSON.stringify(newNode)));
  });
}

// Remove the block at path.
export function removeBlock(comps, compId, path) {
  return updateScript(comps, compId, (script) => {
    const parentPath = path.slice(0, -1);
    const index = path[path.length - 1];
    const parent = getNode(script, parentPath);
    parent.children.splice(index, 1);
  });
}

// Move a block from one position to another.
// fromPath → current path of the block
// toContainerPath → path of the container to insert into
// toIndex → insertion index within that container
export function moveBlock(comps, compId, fromPath, toContainerPath, toIndex) {
  return updateScript(comps, compId, (script) => {
    // Capture the block
    const block = JSON.parse(JSON.stringify(getNode(script, fromPath)));

    // Remove from old position
    const oldParentPath = fromPath.slice(0, -1);
    const oldIndex = fromPath[fromPath.length - 1];
    const oldParent = getNode(script, oldParentPath);
    oldParent.children.splice(oldIndex, 1);

    // Adjust toIndex if inserting into the same container after the removal point
    let adjustedIndex = toIndex;
    if (pathsEqual(oldParentPath, toContainerPath) && oldIndex < toIndex) {
      adjustedIndex--;
    }

    const newContainer = getNode(script, toContainerPath);
    newContainer.children.splice(adjustedIndex, 0, block);
  });
}

// Replace the expression in a slot (sets container.children = [newNode]).
export function setExpression(comps, compId, containerPath, newNode) {
  return updateScript(comps, compId, (script) => {
    const container = getNode(script, containerPath);
    container.children = [JSON.parse(JSON.stringify(newNode))];
  });
}

// Move an expression from its current path to a new container.
export function moveExpression(comps, compId, fromPath, toContainerPath) {
  return updateScript(comps, compId, (script) => {
    const expr = JSON.parse(JSON.stringify(getNode(script, fromPath)));

    // Remove from old slot
    const oldContainerPath = fromPath.slice(0, -1);
    const oldContainer = getNode(script, oldContainerPath);
    oldContainer.children.splice(fromPath[fromPath.length - 1], 1);

    // Set in new slot
    const newContainer = getNode(script, toContainerPath);
    newContainer.children = [expr];
  });
}

// Update a field on a node.
export function setField(comps, compId, path, key, value) {
  return updateScript(comps, compId, (script) => {
    const node = getNode(script, path);
    node[key] = value;
  });
}

// Toggle comment on a node.
export function toggleComment(comps, compId, path) {
  return updateScript(comps, compId, (script) => {
    const node = getNode(script, path);
    node.comment = node.comment === -1 ? '' : -1;
  });
}

// Add or remove a variadic slot (for max/min operators).
export function addVariadicSlot(comps, compId, path) {
  return updateScript(comps, compId, (script) => {
    const node = getNode(script, path);
    node.children.push({ children: [] });
    node.numOfChildren = (node.numOfChildren || node.children.length - 1) + 1;
  });
}

export function removeVariadicSlot(comps, compId, path) {
  return updateScript(comps, compId, (script) => {
    const node = getNode(script, path);
    if (node.children.length > 2) {
      node.children.pop();
      node.numOfChildren = node.children.length;
    }
  });
}

function pathsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}
